import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  Modal,
  Radio,
  Space,
  Typography,
} from 'antd';
import { useMutation } from '@apollo/client/react';
import { useParams } from 'react-router';

import QueryBoundary from 'components/QueryBoundary/QueryBoundary';
import SectionMessage from 'components/SectionMessage/SectionMessage';

import useAgent from 'hooks/useAgent';

import { SAVE_OFAC_REVIEW } from 'mutations/ofacReview';
import { SIZE, STATUS } from 'utilities/constants';
import useApplicationQuery from 'hooks/useApplicationQuery';
import useTimeoutValue from 'hooks/useTimeoutValue';

const OFAC_REVIEW_STATUS = 'OFAC_REVIEW';

const { Text } = Typography;

const OfacReviewPage = () => {
  const agent = useAgent();
  const [form] = Form.useForm();
  const { applicationId } = useParams();
  const { application, applicationQuery } = useApplicationQuery(applicationId);
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useTimeoutValue(false, 2000);
  const [requiresReview, setRequiresReview] = useState(false);
  const selectedOfacReview = Form.useWatch('ofacReview', form);

  const [saveOfacReview, saveOfacReviewMutation] =
    useMutation(SAVE_OFAC_REVIEW);

  useEffect(() => {
    if (
      agent?.isSuperUser &&
      application?.statusReason === OFAC_REVIEW_STATUS
    ) {
      setRequiresReview(true);
    }
  }, [agent, application]);

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    setShowError(false);

    try {
      await saveOfacReview({
        variables: {
          applicationId,
          agentId: agent?.user?.requester_id,
          notes: '',
          outcome: values.ofacReview.toUpperCase(),
        },
      });
      setShowSuccess(true);
    } catch (error) {
      setShowError(true);
    }
  };

  const handleSubmitConfirm = () => {
    if (selectedOfacReview === 'fail') {
      Modal.confirm({
        title: (
          <Text strong>
            Has the BSA/AML officer been alerted to this application?
          </Text>
        ),
        content: <Text>Clicking "Yes" will submit your OFAC review. </Text>,
        onOk: handleSubmit,
        okText: 'Yes',
        cancelText: 'No',
      });
    } else {
      handleSubmit();
    }
  };

  const SubmitButton = (buttonProps) => {
    return (
      <Button
        data-testid="submit-review-button"
        type="primary"
        disabled={!requiresReview}
        loading={saveOfacReviewMutation.loading}
        {...buttonProps}
      >
        Submit
      </Button>
    );
  };

  return (
    <>
      <Card title="OFAC Pause State">
        <Form form={form} initialValues={{}} layout="vertical">
          {showSuccess && (
            <SectionMessage
              data-testid="save-ofac-success-message"
              status={STATUS.SUCCESS}
              size={SIZE.SM}
              text="OFAC review saved successfully"
              visible={showSuccess}
              cover
            />
          )}

          <QueryBoundary
            mode={QueryBoundary.MODE.MESSAGE}
            loadingMessage="Loading OFAC Pause State"
            loadingOverride={applicationQuery.loading || !agent.loaded}
            query={applicationQuery}
          >
            {agent.isSuperUser ? (
              <>
                <div style={{ paddingBottom: 'var(--spacing)' }}>
                  <Form.Item
                    data-testid="ofac-review-field"
                    name="ofacReview"
                    label="What is the outcome of the OFAC sanctions list review?"
                    style={{ marginBottom: 'var(--spacing)' }}
                    rules={[
                      {
                        message: 'Please make a selection',
                        required: true,
                      },
                    ]}
                  >
                    <Radio.Group>
                      <Space orientation="vertical">
                        <Radio data-testid="clear" value="pass">
                          Clear: Applicant is not a match to an individual on
                          sanctions watchlists.
                        </Radio>
                        <Radio data-testid="watchlist-match" value="fail">
                          Watchlist match: Applicant is a true match to
                          sanctions watchlists and should not be allowed to
                          continue with their application.
                        </Radio>
                      </Space>
                    </Radio.Group>
                  </Form.Item>

                  {requiresReview && (
                    <Alert
                      banner
                      data-testid="contact-bsa-officer-message"
                      type="warning"
                      style={{ maxWidth: 'fit-content' }}
                      title="If an applicant is flagged as a match, contact our BSA/AML Officer (Amy Bracht) prior to declining the application"
                    />
                  )}

                  {!requiresReview && (
                    <Alert
                      banner
                      data-testid="no-review-message"
                      type="warning"
                      style={{ maxWidth: 'fit-content' }}
                      title="This applicant is not in need of an OFAC review."
                    />
                  )}

                  {showError && (
                    <Alert
                      banner
                      type="error"
                      style={{ maxWidth: 'fit-content' }}
                      title="Failed to submit OFAC review. Please try again."
                    />
                  )}
                </div>

                <SubmitButton onClick={handleSubmitConfirm} />
              </>
            ) : (
              <SectionMessage
                data-testid="no-ofac-access-message"
                status={STATUS.ERROR}
                size={SIZE.SM}
                text="Sorry, you lack the permissions to execute an OFAC Review"
              />
            )}
          </QueryBoundary>
        </Form>
      </Card>
    </>
  );
};

export default OfacReviewPage;
